import { useRouter } from 'next/navigation';
import { useRef, useState } from 'react';
import { toast } from 'react-toastify';

import {
  updateBannerCollection,
  updateCollection,
  updatePictureCollection,
} from '@/services/collection';
import { ProfileSocial } from '@/types';
import type { LinkSocialProfile } from '@/types/user';

const useEditCollection = () => {
  const [loading, setLoading] = useState(false);
  const router = useRouter();

  const [previewThumbnail, setPreviewThumbnail] = useState('');
  const [previewCover, setPreviewCover] = useState('');
  const thumbnailRef = useRef<File>();
  const coverRef = useRef<File>();

  const handleChangeFile = (file: File, type: 'thumbnail' | 'cover') => {
    if (file.size / 1024 / 1024 > 20) {
      toast.warning('Maximum size is 20MB');
      return;
    }
    const url = URL.createObjectURL(file);
    if (type === 'thumbnail') {
      thumbnailRef.current = file;
      setPreviewThumbnail(url);
    } else {
      coverRef.current = file;
      setPreviewCover(url);
    }
  };
  const handleRemoveFile = (type: 'thumbnail' | 'cover') => {
    if (type === 'thumbnail') {
      thumbnailRef.current = undefined;
      setPreviewThumbnail('');
    } else {
      coverRef.current = undefined;
      setPreviewCover('');
    }
  };

  const handleUpdateCollection = async (
    collectionId: number,
    name?: string,
    description?: string,
    links?: LinkSocialProfile[],
    tags?: [
      {
        id: number;
        content: string;
      },
    ],
  ) => {
    try {
      setLoading(true);
      await updateCollection(collectionId, { name, description, links, tags });
      toast.success('Update collection successfully!');

      if (collectionId && thumbnailRef.current) {
        const formData = new FormData();
        formData.append('picture', thumbnailRef.current, 'thumbnail');
        const resUpdate = await updatePictureCollection(collectionId, formData);
        if (!resUpdate.data?.id) {
          toast.error('Error, please to try!');
          return;
        }
      }
      if (collectionId && coverRef.current) {
        const formData = new FormData();
        formData.append('picture', coverRef.current, 'banner');
        const resUpdate = await updateBannerCollection(collectionId, formData);
        if (!resUpdate.data?.id) {
          toast.error('Error, please to try!');
          return;
        }
      }
      setLoading(false);
      router.refresh();
    } catch (error: any) {
      setLoading(false);
      toast.error(error?.message);
    }
  };

  const getLinks = (data: any): LinkSocialProfile[] => {
    const links = [
      {
        type: ProfileSocial.facebook,
        url: data[ProfileSocial.facebook] || '',
      },
      {
        type: ProfileSocial.twitter,
        url: data[ProfileSocial.twitter] || '',
      },
      {
        type: ProfileSocial.telegram,
        url: data[ProfileSocial.telegram] || '',
      },
      {
        type: ProfileSocial.instagram,
        url: data[ProfileSocial.instagram] || '',
      },
      {
        type: ProfileSocial.discord,
        url: data[ProfileSocial.discord] || '',
      },
    ];

    return links.filter((link) => link?.url !== '');
  };

  return {
    loading,
    handleUpdateCollection,
    previewThumbnail,
    previewCover,
    handleChangeFile,
    handleRemoveFile,
    getLinks,
  };
};

export default useEditCollection;
